import {
  adminApiClient,
  extractDownloadFilename,
  triggerBrowserDownload,
  resolveBlobApiErrorMessage,
  resolveApiErrorMessage,
} from '@/services/admin/baseService'
import type {
  ApiResponse,
  OrderListItem,
  PaginatedResponse,
  OrderLifecycleResponse,
  OrderDocumentDetail,
  OrderDocumentType,
  OrderDocumentsResponse,
  AdminOrderSideEffectJobCursorList,
  AdminOrderAddressDoubleCheck,
  AdminOrderStateConsistency,
  SolanaOrderGovernanceChainState,
  EvmOperationEnvelope,
  EvmPendingOperationItem,
  OrderSideEffectJobStatus,
} from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import {
  buildPaginatedResponse,
  extractArrayFromApiPayload,
} from '@/services/admin/paginatedResponseHelpers'
import { evmOperationService } from '@/services/admin/evmOperationService'

const mapOrderListRecord = (record: any): OrderListItem => ({
  orderId: record?.orderId ?? record?.order_id ?? record?.id ?? '',
  merchantBusinessId:
    record?.merchant_business_id ?? record?.merchantBusinessId ?? record?.merchantId ?? '',
  merchantId: record?.merchant_id ?? record?.merchantId ?? undefined,
  chainId: record?.chain_id ?? record?.chainId ?? '',
  chainLabel: record?.chain_label ?? record?.chainLabel ?? record?.chain_name ?? record?.chainName ?? '',
  chainCode: record?.chain_code ?? record?.chainCode ?? undefined,
  chainName: record?.chain_name ?? record?.chainName ?? undefined,
  chainNumericId: record?.chain_numeric_id ?? record?.chainNumericId ?? undefined,
  isTestnet: record?.is_testnet ?? record?.isTestnet ?? undefined,
  chainCompatibility: record?.chainCompatibility ?? record?.chain_compatibility ?? undefined,
  solanaOrderPauseSupported:
    record?.solanaOrderPauseSupported ?? record?.solana_order_pause_supported ?? undefined,
  amountUsd: record?.amount_usd ?? record?.amountUsd ?? '',
  tokenSymbol: record?.token_symbol ?? record?.tokenSymbol ?? '',
  paymentTokenAddress:
    record?.paymentTokenAddress ?? record?.payment_token_address ?? record?.token_address ?? undefined,
  programId: record?.programId ?? record?.program_id ?? undefined,
  orderStatePda: record?.orderStatePda ?? record?.order_state_pda ?? undefined,
  orderVaultAuthorityPda:
    record?.orderVaultAuthorityPda ?? record?.order_vault_authority_pda ?? undefined,
  paidAmountUsd: record?.paid_amount_usd ?? record?.paidAmountUsd ?? '0',
  settledAmountUsd: record?.settled_amount_usd ?? record?.settledAmountUsd ?? '0',
  paymentAddress: record?.payment_address ?? record?.paymentAddress ?? null,
  status: record?.status ?? record?.status_id ?? '',
  paymentConfirmationState:
    record?.payment_confirmation_state ?? record?.paymentConfirmationState ?? 'unknown',
  createdAt: record?.created_at ?? record?.createdAt ?? '',
  updatedAt: record?.updated_at ?? record?.updatedAt ?? '',
  expiresAt: record?.expires_at ?? record?.expiresAt ?? null,
  hasRefunded: Boolean(record?.has_refunded ?? record?.hasRefunded ?? false),
  notesToPublic: record?.notes_to_public ?? record?.notesToPublic ?? null,
})

const normalizePaginatedResponse = <T>(
  payload: ApiResponse<any> | undefined,
  options: { keys?: string[]; defaultPage?: number; defaultLimit?: number } = {}
): PaginatedResponse<T> => {
  const payloadAny = payload as any
  const items = extractArrayFromApiPayload<T>(payloadAny, {
    keys: options.keys,
    candidateKeys: ['items', 'records', 'deployments', 'orders'],
    includePagination: true,
  })
  return buildPaginatedResponse(payload, items, options)
}

export interface OrderListParams {
  page?: number
  pageSize?: number
  limit?: number
  search?: string
  status?: string
  merchant_id?: string
  chain_id?: string
  sortBy?: 'createdAt' | 'status' | 'amount'
  sortOrder?: 'asc' | 'desc'
  createdAtFrom?: string
  createdAtTo?: string
}

export type PaymentMonitoringTriggerOrder = {
  orderId: string
  stateVersion: number
  lastOnchainScanAt: string | null
}

export type PaymentMonitoringTriggerResponse = {
  success: boolean
  message: string
  acceptedAt: string
  mode: NetworkEnvironment
  executionMode: 'queued'
  ordersQueued: number
  batchesQueued: number
  batchIds: string[]
  orders: PaymentMonitoringTriggerOrder[]
  selectionLimit: number
  truncated: boolean
  skipped: {
    recentlyScannedOrderIds: string[]
    notEligibleIdentifiers: string[]
    notFoundIdentifiers: string[]
  }
}

export type PaymentMonitoringExecutionOutcome =
  | 'queued_or_retrying'
  | 'processed_no_change'
  | 'expired_unpaid'
  | 'underpaid'
  | 'fullpaid'
  | 'overpaid'
  | 'state_changed'
  | 'state_applied_cache_pending'
  | 'unresolved'

export type PaymentMonitoringExecutionStatusOrder = {
  orderId: string
  orderBusinessId: string | null
  baselineStateVersion: number
  stateVersion: number | null
  statusId: string | null
  onchainBalance: string | null
  lastOnchainScanAt: string | null
  event: {
    id: string
    type: string
    sequence: number
    createdAt: string
  } | null
  cacheSync: {
    status: OrderSideEffectJobStatus
    error: string | null
  } | null
  outcome: PaymentMonitoringExecutionOutcome
  complete: boolean
}

export type PaymentMonitoringExecutionStatus = {
  mode: NetworkEnvironment
  acceptedAt: string
  observedAt: string
  complete: boolean
  orders: PaymentMonitoringExecutionStatusOrder[]
}

type ContractGovernanceAction = 'emergency_pause' | 'emergency_resume' | 'close_contract'
type OrderStatusAction = 'compliance_hold' | 'closed'
type AccountCleanupResponse = {
  orderId?: string
  internalOrderId?: string
  recordId?: string | null
  queued?: boolean
  status?: string
  reason?: string | null
  triggerSource?: string
}
type AccountCleanupVerificationResponse = {
  orderId?: string
  internalOrderId?: string
  recordId?: string
  verificationStatus?: string
  verificationNotes?: string
  orderClosed?: boolean
  verifiedAt?: string | null
}
type AccountCleanupRetryResponse = {
  orderId?: string
  internalOrderId?: string
  sourceRecordId?: string
  recordId?: string | null
  queued?: boolean
  status?: string
  reason?: string | null
  triggerSource?: string
}

export const orderService = {
  createEvmGovernanceOperation(
    adminId: string,
    orderId: string,
    deploymentId: string,
    action: ContractGovernanceAction,
    notes: string | undefined,
    mode: NetworkEnvironment
  ): EvmPendingOperationItem {
    return evmOperationService.createPending(adminId, mode, {
      operationType: 'evm_governance',
      requestBody: notes ? { notes } : {},
      clientResourceLocator: { orderId, action },
      resourceType: 'governance_deployment',
      resourceId: deploymentId,
    })
  },

  async submitEvmGovernanceOperation(
    adminId: string,
    item: EvmPendingOperationItem,
    mode: NetworkEnvironment
  ): Promise<EvmOperationEnvelope> {
    return evmOperationService.submitPending(adminId, mode, item)
  },

  async getAllOrders(
    params?: OrderListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<OrderListItem>> {
    const searchParams = new URLSearchParams()

    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.pageSize) {
      searchParams.append('pageSize', params.pageSize.toString())
    } else if (params?.limit) {
      searchParams.append('pageSize', params.limit.toString())
    }
    if (params?.search) searchParams.append('search', params.search)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.merchant_id) searchParams.append('merchant_id', params.merchant_id)
    if (params?.chain_id) searchParams.append('chain_id', params.chain_id)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)
    if (params?.createdAtFrom) searchParams.append('createdAtFrom', params.createdAtFrom)
    if (params?.createdAtTo) searchParams.append('createdAtTo', params.createdAtTo)

    const query = searchParams.toString()
    const url = query ? `/admin/orders?${query}` : '/admin/orders'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    const normalized = normalizePaginatedResponse<OrderListItem>(response.data, {
      keys: ['orders'],
      defaultPage: params?.page,
      defaultLimit: params?.pageSize ?? params?.limit,
    })

    return {
      success: normalized.success,
      data: (normalized.data ?? []).map(mapOrderListRecord),
      error: normalized.error,
      message: normalized.message,
      pagination: normalized.pagination,
    }
  },

  async exportCsv(
    params?: Omit<OrderListParams, 'page' | 'pageSize' | 'limit'>,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>('/admin/orders/export', {
        params,
        responseType: 'blob',
        ...buildModeAwareConfig(mode),
      })

      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `orders-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export orders'))
    }
  },

  async getOrderLifecycle(
    orderId: string,
    include?: string[],
    mode?: NetworkEnvironment
  ): Promise<OrderLifecycleResponse> {
    if (!orderId) {
      throw new Error('orderId is required')
    }

    const params = include && include.length ? { include: include.join(',') } : undefined
    const response = await adminApiClient.get<ApiResponse<OrderLifecycleResponse>>(
      `/admin/orders/${orderId}/lifecycle`,
      {
        ...(params ? { params } : {}),
        ...buildModeAwareConfig(mode),
      }
    )

    if (!response.data?.success || !response.data?.data) {
      throw new Error('Failed to fetch order lifecycle')
    }

    return response.data.data
  },

  async getSolanaOrderGovernanceState(
    orderId: string,
    mode?: NetworkEnvironment
  ): Promise<SolanaOrderGovernanceChainState> {
    if (!orderId) {
      throw new Error('orderId is required')
    }
    const response = await adminApiClient.get<ApiResponse<SolanaOrderGovernanceChainState>>(
      `/admin/orders/${encodeURIComponent(orderId)}/solana-governance-state`,
      buildModeAwareConfig(mode)
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.error || 'Failed to load Solana governance state')
    }
    return response.data.data
  },

  async getOrderSideEffectJobs(
    orderId: string,
    params?: {
      limit?: number
      cursor?: string
      status?: string
      jobType?: string
    },
    mode?: NetworkEnvironment
  ): Promise<AdminOrderSideEffectJobCursorList> {
    const response = await adminApiClient.get<ApiResponse<AdminOrderSideEffectJobCursorList>>(
      `/admin/orders/${encodeURIComponent(orderId)}/side-effect-jobs`,
      {
        params,
        ...buildModeAwareConfig(mode),
      }
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.error || 'Failed to load order side-effect jobs')
    }
    return response.data.data
  },

  async getOrderStateConsistency(
    orderId: string,
    mode?: NetworkEnvironment
  ): Promise<AdminOrderStateConsistency> {
    const response = await adminApiClient.get<ApiResponse<AdminOrderStateConsistency>>(
      `/admin/orders/${encodeURIComponent(orderId)}/state-consistency`,
      buildModeAwareConfig(mode)
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.error || 'Failed to load order state consistency')
    }
    return response.data.data
  },

  async getOrderAddressDoubleCheck(
    orderId: string,
    mode?: NetworkEnvironment
  ): Promise<AdminOrderAddressDoubleCheck> {
    const response = await adminApiClient.get<ApiResponse<AdminOrderAddressDoubleCheck>>(
      `/admin/orders/${encodeURIComponent(orderId)}/address-double-check`,
      buildModeAwareConfig(mode)
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.error || 'Failed to load order address double-check data')
    }
    return response.data.data
  },

  async getOrderDocuments(
    orderId: string,
    mode?: NetworkEnvironment
  ): Promise<OrderDocumentsResponse> {
    if (!orderId) {
      throw new Error('orderId is required')
    }

    const response = await adminApiClient.get<ApiResponse<OrderDocumentsResponse>>(
      `/admin/orders/${orderId}/documents`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.success || !response.data?.data) {
      throw new Error('Failed to fetch order documents')
    }

    return response.data.data
  },

  async getOrderDocument(
    orderId: string,
    documentType: OrderDocumentType,
    mode?: NetworkEnvironment
  ): Promise<OrderDocumentDetail> {
    if (!orderId) {
      throw new Error('orderId is required')
    }

    const response = await adminApiClient.get<ApiResponse<OrderDocumentDetail>>(
      `/admin/orders/${orderId}/documents/${documentType}`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.success || !response.data?.data) {
      throw new Error(`Failed to fetch ${documentType} document`)
    }

    return response.data.data
  },

  async downloadOrderDocumentPdf(
    orderId: string,
    documentType: OrderDocumentType,
    mode?: NetworkEnvironment,
    fallbackFilename?: string
  ): Promise<void> {
    if (!orderId) {
      throw new Error('orderId is required')
    }

    try {
      const response = await adminApiClient.get<Blob>(
        `/admin/orders/${orderId}/documents/${documentType}/pdf`,
        {
          responseType: 'blob',
          ...buildModeAwareConfig(mode),
        }
      )

      triggerBrowserDownload(
        response.data,
        extractDownloadFilename(
          response.headers['content-disposition'],
          fallbackFilename ?? `${documentType}.pdf`
        )
      )
    } catch (error) {
      throw new Error(
        await resolveBlobApiErrorMessage(error, `Failed to download ${documentType}`)
      )
    }
  },

  async retryOrderDocument(
    orderId: string,
    documentType: OrderDocumentType,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<{
    orderId?: string
    documentType?: OrderDocumentType
    status?: string
    triggerSource?: string
  }>> {
    if (!orderId) {
      throw new Error('orderId is required')
    }

    try {
      const response = await adminApiClient.post<ApiResponse<{
        orderId?: string
        documentType?: OrderDocumentType
        status?: string
        triggerSource?: string
      }>>(
        `/admin/orders/${orderId}/documents/${documentType}/retry`,
        {},
        buildModeAwareConfig(mode)
      )

      if (!response.data) {
        throw new Error(`Failed to retry ${documentType} document`)
      }

      return response.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, `Failed to retry ${documentType}`))
    }
  },

  async triggerAccountCleanup(
    orderId: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<AccountCleanupResponse>> {
    if (!orderId) {
      throw new Error('orderId is required')
    }

    const response = await adminApiClient.post<ApiResponse<AccountCleanupResponse>>(
      `/admin/orders/${orderId}/account-cleanup`,
      {},
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to trigger account cleanup')
    }

    return response.data
  },

  async verifyAccountCleanup(
    orderId: string,
    recordId: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<AccountCleanupVerificationResponse>> {
    if (!orderId) {
      throw new Error('orderId is required')
    }
    if (!recordId) {
      throw new Error('recordId is required')
    }

    const response = await adminApiClient.post<ApiResponse<AccountCleanupVerificationResponse>>(
      `/admin/orders/${orderId}/account-cleanup/${recordId}/verify`,
      {},
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to verify account cleanup')
    }

    return response.data
  },

  async retryAccountCleanup(
    orderId: string,
    recordId: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<AccountCleanupRetryResponse>> {
    if (!orderId) {
      throw new Error('orderId is required')
    }
    if (!recordId) {
      throw new Error('recordId is required')
    }

    const response = await adminApiClient.post<ApiResponse<AccountCleanupRetryResponse>>(
      `/admin/orders/${orderId}/account-cleanup/${recordId}/retry`,
      {},
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to retry account cleanup')
    }

    return response.data
  },

  async onholdOrCloseOrder(
    orderId: string,
    payload: {
      status: OrderStatusAction
      auditLog: string
      notesToPublic?: string
      closeContract?: boolean
    },
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<{
    orderId?: string
    status?: string
    closeContractRequested?: boolean
    closeContractQueued?: boolean
    closeContractSkipReason?: string | null
    alreadyInStatus?: boolean
    solanaGovernanceRequestId?: string | null
    solanaGovernanceStatus?: string | null
  }>> {
    if (!orderId) {
      throw new Error('orderId is required')
    }
    if (!payload?.status) {
      throw new Error('status is required')
    }
    if (!payload?.auditLog) {
      throw new Error('auditLog is required')
    }

    const modeConfig = buildModeAwareConfig(mode)
    const response = await adminApiClient.post<ApiResponse<{
      orderId?: string
      status?: string
      closeContractRequested?: boolean
      closeContractQueued?: boolean
      closeContractSkipReason?: string | null
      alreadyInStatus?: boolean
      solanaGovernanceRequestId?: string | null
      solanaGovernanceStatus?: string | null
    }>>(
      `/admin/orders/${orderId}/onhold-or-close`,
      payload,
      {
        ...modeConfig,
        headers: {
          ...(modeConfig?.headers ?? {}),
          'Idempotency-Key': crypto.randomUUID(),
        },
      }
    )

    if (!response.data) {
      throw new Error('Failed to update order status')
    }

    return response.data
  },

  async resumeFromHoldOrder(
    orderId: string,
    payload: {
      auditLog: string
      notesToPublic?: string
    },
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<{
    orderId?: string
    status?: string
    released?: boolean
    releasePending?: boolean
    releaseResult?: 'released' | 'already_released'
    confirmedAmountInSmallestUnit?: string
    sideEffectJobsEnqueued?: number
    governanceCompatibility?: 'evm' | 'solana'
    governanceRequestId?: string
    governanceStatus?: string
  }>> {
    if (!orderId) {
      throw new Error('orderId is required')
    }
    if (!payload?.auditLog) {
      throw new Error('auditLog is required')
    }

    const modeConfig = buildModeAwareConfig(mode)
    const response = await adminApiClient.post<ApiResponse<{
      orderId?: string
      status?: string
      released?: boolean
      releasePending?: boolean
      releaseResult?: 'released' | 'already_released'
      confirmedAmountInSmallestUnit?: string
      sideEffectJobsEnqueued?: number
      governanceCompatibility?: 'evm' | 'solana'
      governanceRequestId?: string
      governanceStatus?: string
    }>>(
      `/admin/orders/${orderId}/resume-from-hold`,
      payload,
      {
        ...modeConfig,
        headers: {
          ...(modeConfig?.headers ?? {}),
          'Idempotency-Key': crypto.randomUUID(),
        },
      }
    )

    if (!response.data) {
      throw new Error('Failed to resume order status')
    }

    return response.data
  },

  async triggerContractGovernance(
    orderId: string,
    action: ContractGovernanceAction,
    payload?: { notes?: string },
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<{
    queued: boolean
    action: ContractGovernanceAction
    guard_mode?: string
    trigger_source?: string
    orderId?: string
    deploymentId?: string
    contractAddress?: string
    notes?: string | null
    reason?: string | null
    refundable_balance?: string
    refundable_balance_formatted?: string
    token_decimals?: number
    governanceRequestId?: string
    governanceStatus?: string
    pauseSequence?: number
  }>> {
    if (!orderId) {
      throw new Error('orderId is required')
    }

    const endpointMap: Record<ContractGovernanceAction, string> = {
      emergency_pause: 'emergency-pause',
      emergency_resume: 'emergency-resume',
      close_contract: 'close-contract',
    }

    const endpoint = endpointMap[action]
    if (!endpoint) {
      throw new Error(`Unsupported governance action: ${action}`)
    }

    const body = payload?.notes ? { notes: payload.notes } : {}
    const modeConfig = buildModeAwareConfig(mode)
    const response = await adminApiClient.post<ApiResponse<{
      queued: boolean
      action: ContractGovernanceAction
      guard_mode?: string
      trigger_source?: string
      orderId?: string
      deploymentId?: string
      contractAddress?: string
      notes?: string | null
      reason?: string | null
      refundable_balance?: string
      refundable_balance_formatted?: string
      token_decimals?: number
      governanceRequestId?: string
      governanceStatus?: string
      pauseSequence?: number
    }>>(
      `/admin/orders/${orderId}/${endpoint}`,
      body,
      {
        ...modeConfig,
        headers: {
          ...(modeConfig?.headers ?? {}),
          'Idempotency-Key': crypto.randomUUID(),
        },
      }
    )

    if (!response.data) {
      throw new Error('Failed to trigger governance action')
    }

    return response.data
  },

  async triggerPaymentMonitoring(params: {
    merchantId?: string
    orderIds?: string[]
    force?: boolean
  }, mode?: NetworkEnvironment): Promise<PaymentMonitoringTriggerResponse> {
    const response = await adminApiClient.post<PaymentMonitoringTriggerResponse>(
      '/admin/trigger-monitoring',
      params,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to enqueue payment monitoring')
    }
    return response.data
  },

  async getPaymentMonitoringExecutionStatus(
    trigger: Pick<PaymentMonitoringTriggerResponse, 'acceptedAt' | 'orders'>,
    mode: NetworkEnvironment,
    signal?: AbortSignal
  ): Promise<PaymentMonitoringExecutionStatus> {
    const response = await adminApiClient.post<{
      success: boolean
      data?: PaymentMonitoringExecutionStatus
    }>(
      '/admin/monitor/payment-monitoring/status',
      {
        acceptedAt: trigger.acceptedAt,
        orders: trigger.orders.map(({ orderId, stateVersion }) => ({
          orderId,
          stateVersion,
        })),
      },
      {
        ...buildModeAwareConfig(mode),
        signal,
      }
    )

    if (!response.data?.success || !response.data.data) {
      throw new Error('Failed to read payment monitoring execution status')
    }
    if (response.data.data.mode !== mode) {
      throw new Error('Payment monitoring execution status mode mismatch')
    }
    return response.data.data
  },

  async updatePaymentConfirmations(params?: {
    chainId?: string
  }, mode?: NetworkEnvironment): Promise<{
    queuedCount: number
    chainId?: string
  }> {
    const response = await adminApiClient.post<{
      success: boolean
      message: string
      timestamp: string
      environment: string
      duration: string
      processing: {
        mode: string
        requestedBy: string
      }
      filter: {
        chainId: string
        chains: string[]
      }
      stats: {
        totalChainsRequested: number
        queuedCount: number
        processingNote: string
      }
    }>('/admin/payment-confirmations/update', params || {}, buildModeAwareConfig(mode))
    
    const backendData = response.data
    const queuedCount = backendData?.stats?.queuedCount
    const chainId = backendData?.filter?.chainId
    if (
      backendData?.success !== true
      || typeof queuedCount !== 'number'
      || !Number.isSafeInteger(queuedCount)
      || queuedCount < 0
      || typeof chainId !== 'string'
      || chainId.trim().length === 0
    ) {
      throw new Error('Invalid response from payment confirmation update API')
    }

    return {
      queuedCount,
      chainId: chainId === 'all' ? undefined : chainId,
    }
  },
}
